import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function Mesas() {
  const [mesas, setMesas] = useState([]);
  const [meseros, setMeseros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAbrirModal, setShowAbrirModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [meseroSeleccionado, setMeseroSeleccionado] = useState('');
  const [formData, setFormData] = useState({ numero: '', zona: '' });
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [mesasRes, meserosRes] = await Promise.all([
        api.get('/mesas'),
        api.get('/meseros'),
      ]);
      setMesas(mesasRes.data);
      setMeseros(meserosRes.data);
    } catch (error) {
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirMesa = (mesa) => {
    if (meseros.length === 0) {
      Swal.fire('Aviso', 'No hay meseros registrados. Agrega un mesero primero.', 'warning');
      return;
    }
    setMesaSeleccionada(mesa);
    setMeseroSeleccionado('');
    setShowAbrirModal(true);
  };

  const handleVerPedido = async (mesa) => {
    try {
      const res = await api.get(`/ordenes/mesa/${mesa.id}`);
      navigate(`/ordenes/${res.data.id}`);
    } catch (error) {
      Swal.fire('Error', 'No se pudo encontrar la orden activa', 'error');
    }
  };

  const confirmarAbrirMesa = async () => {
    if (!meseroSeleccionado) {
      Swal.fire('Error', 'Selecciona un mesero', 'error');
      return;
    }
    try {
      const res = await api.post('/ordenes', {
        mesa_id: mesaSeleccionada.id,
        mesero_id: meseroSeleccionado,
      });
      setShowAbrirModal(false);
      navigate(`/ordenes/${res.data.id}`);
    } catch (error) {
      Swal.fire('Error', 'No se pudo abrir la mesa', 'error');
    }
  };

  const handleGuardar = async () => {
    if (!formData.numero) {
      Swal.fire('Error', 'El número de mesa es requerido', 'error');
      return;
    }
    try {
      await api.post('/mesas', formData);
      Swal.fire('Éxito', 'Mesa creada correctamente', 'success');
      setShowModal(false);
      setFormData({ numero: '', zona: '' });
      cargarDatos();
    } catch (error) {
      Swal.fire('Error', 'No se pudo crear la mesa', 'error');
    }
  };

  const handleEliminar = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar mesa?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/mesas/${id}`);
        Swal.fire('Éxito', 'Mesa eliminada correctamente', 'success');
        setShowConfigModal(false);
        cargarDatos();
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar la mesa', 'error');
      }
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'disponible': return '#28a745';
      case 'ocupada': return '#dc3545';
      case 'cuenta_pendiente': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'disponible': return 'DISPONIBLE';
      case 'ocupada': return 'OCUPADA';
      case 'cuenta_pendiente': return 'CUENTA PENDIENTE';
      default: return estado;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Mesas</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + Nueva Mesa
        </button>
      </div>

      {mesas.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <h5>No hay mesas registradas</h5>
        </div>
      ) : (
        <div className="row g-3">
          {mesas.map((mesa) => (
            <div key={mesa.id} className="col-6 col-md-4 col-lg-3">
              <div
                className="card h-100 text-center"
                style={{
                  borderTop: `4px solid ${getEstadoColor(mesa.estado)}`,
                  borderRadius: '12px',
                  cursor: mesa.estado === 'ocupada' ? 'pointer' : 'default',
                  position: 'relative',
                }}
                onClick={() => mesa.estado === 'ocupada' && handleVerPedido(mesa)}
              >
                {/* Botón configuración */}
                <button
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    background: 'none', border: 'none',
                    fontSize: '18px', cursor: 'pointer',
                    color: '#6c757d', padding: '2px 6px',
                    borderRadius: '50%',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMesaSeleccionada(mesa);
                    setShowConfigModal(true);
                  }}
                  title="Configurar mesa"
                >
                  ⚙️
                </button>

                <div className="card-body">
                  <h3 className="card-title fw-bold">Mesa {mesa.numero}</h3>
                  {mesa.zona && <p className="text-muted mb-2"><small>{mesa.zona}</small></p>}
                  <span className="badge mb-3" style={{ backgroundColor: getEstadoColor(mesa.estado) }}>
                    {getEstadoTexto(mesa.estado)}
                  </span>
                  <div className="d-flex flex-column gap-2 align-items-center">
                    {mesa.estado === 'ocupada' ? (
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={(e) => { e.stopPropagation(); handleVerPedido(mesa); }}
                      >
                        👁 Ver Pedido
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={(e) => { e.stopPropagation(); handleAbrirMesa(mesa); }}
                      >
                        Abrir
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Configurar Mesa */}
      {showConfigModal && mesaSeleccionada && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⚙️ Mesa {mesaSeleccionada.numero}</h5>
                <button className="btn-close" onClick={() => setShowConfigModal(false)} />
              </div>
              <div className="modal-body d-flex flex-column gap-2">
                <button
                  className="btn btn-danger w-100"
                  onClick={() => handleEliminar(mesaSeleccionada.id)}
                >
                  🗑️ Eliminar Mesa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nueva Mesa */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nueva Mesa</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Número de Mesa *</label>
                  <input type="text" className="form-control" placeholder="Ej: 1, 2, VIP1" value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Zona (opcional)</label>
                  <input type="text" className="form-control" placeholder="Ej: Sala principal, Terraza" value={formData.zona} onChange={(e) => setFormData({ ...formData, zona: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleGuardar}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Abrir Mesa */}
      {showAbrirModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Abrir Mesa {mesaSeleccionada?.numero}</h5>
                <button className="btn-close" onClick={() => setShowAbrirModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Selecciona el mesero *</label>
                  <select className="form-select" value={meseroSeleccionado} onChange={(e) => setMeseroSeleccionado(e.target.value)}>
                    <option value="">-- Seleccionar mesero --</option>
                    {meseros.map((m) => (
                      <option key={m.id} value={m.id}>{m.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAbrirModal(false)}>Cancelar</button>
                <button className="btn btn-success" onClick={confirmarAbrirMesa}>Abrir Mesa</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}